(function () {
  'use strict';

  const MID_FLOW_RESUME_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const ASSESSMENT_REQUEST_TIMEOUT_MS = 90 * 1000;
  const CONTACT_CAPTURE_REQUEST_TIMEOUT_MS = 20 * 1000;

  function clearAssessmentRequestTimeout(instance) {
    if (!instance._assessmentTimeoutId) return;
    clearTimeout(instance._assessmentTimeoutId);
    instance._assessmentTimeoutId = null;
  }

  function armAssessmentRequestTimeout(instance) {
    clearAssessmentRequestTimeout(instance);
    instance._assessmentTimeoutId = setTimeout(function abortTimedOutAssessment() {
      if (!instance._assessmentAbort || instance._assessmentAbort.signal.aborted) return;
      try {
        instance._assessmentAbort.abort('request-timeout');
      } catch (error) {
        instance._assessmentAbort.abort();
      }
    }, ASSESSMENT_REQUEST_TIMEOUT_MS);
  }

  function createContactCaptureDeadline(controller) {
    let timeoutId = null;
    const promise = new Promise((_, reject) => {
      timeoutId = setTimeout(function abortTimedOutContactCapture() {
        try {
          controller.abort('request-timeout');
        } catch (error) {
          controller.abort();
        }

        const timeoutError = new Error('Contact capture request timed out');
        timeoutError.name = 'AbortError';
        timeoutError.errorCode = 'request_timeout';
        reject(timeoutError);
      }, CONTACT_CAPTURE_REQUEST_TIMEOUT_MS);
    });

    return {
      promise,
      clear() {
        if (timeoutId === null) return;
        clearTimeout(timeoutId);
        timeoutId = null;
      },
    };
  }

  function nextAssessmentRequestId(instance) {
    instance._assessmentRequestId = (Number(instance._assessmentRequestId) || 0) + 1;
    return instance._assessmentRequestId;
  }

  function isCurrentAssessmentRequest(instance, requestId) {
    return requestId === instance._assessmentRequestId;
  }

  function fireAssessmentEarly(instance) {
    if (hasRenderableAssessment(instance._assessmentResult)) {
      return Promise.resolve(instance._assessmentResult);
    }
    if (instance._assessmentPromise) return instance._assessmentPromise;

    const requestId = nextAssessmentRequestId(instance);
    instance._assessmentResult = null;
    instance._assessmentError = null;

    instance._assessmentPromise = fetchAssessmentAsync(instance)
      .then((result) => {
        if (!isCurrentAssessmentRequest(instance, requestId)) return null;
        instance._assessmentResult = result;
        return result;
      })
      .catch((error) => {
        if (!isCurrentAssessmentRequest(instance, requestId)) return null;
        instance._assessmentError = error;
        return null;
      });

    return instance._assessmentPromise;
  }

  function buildAssessmentRequestBody(instance, locale) {
    return {
      session_id: instance._ensureSessionId(),
      conversation_history: instance.conversationHistory,
      products: instance._productCatalog,
      provider: instance.config.modelProvider || 'openai',
      ...instance._buildWorkerContextPayload(locale),
      ...getOfferSettingsPayload(instance),
      debug: !!instance.config.debug
    };
  }

  // Spec: docs/storefront-integration.md — Change 2.
  // Decide whether stream fallure should drop us back to the JSON endpoint.
  // Once the stream emits an `error` event the worker has already given
  // up server-side, so fallback won't help. HTTP/protocol failures (route
  // unavailable, connection dropped before any event, body unreadable on
  // an old browser) are retried via /api/assessment.
  function shouldFallbackFromStream(error) {
    if (!error || error.name === 'AbortError') return false;
    if (error.streamFinalError) return false;
    if (error.status === 409 || error.status === 429) return false;
    const code = String(error.errorCode || error.code || '').trim();
    if (code === 'assessment_already_running' || code.indexOf('rate_limited_') === 0) return false;
    return Boolean(
      error.streamHttpFailure
      || error.streamUnavailable
      || error.streamProtocolError
      || error.streamNetworkFailure
    );
  }

  // Live preview hook — pipes streaming progress into the existing wait
  // screen's [data-wait-stream] element. Keeps the existing wait UX
  // (story / moments / progress meter) intact and adds copy that reflects
  // what the worker is doing right now ("Composing your headline...",
  // "Drafting your routine..."). Best-effort; if the wait UI isn't
  // mounted (e.g. stream initiated before wait shows), no-op.
  function applyStreamPreviewCopy(instance, eventName, data) {
    const t = instance.config?.i18n || {};
    let copy = '';
    if (eventName === 'accepted') {
      copy = t.assessmentStreamAccepted || t.phaseGenerating || '';
    } else if (eventName === 'cache_hit') {
      copy = t.assessmentStreamCacheHit || t.assessmentReady || '';
    } else if (eventName === 'model_output_started') {
      copy = t.assessmentStreamComposing || t.phaseGenerating || '';
    } else if (eventName === 'partial_assessment') {
      const fields = (data && data.fields) || {};
      if (fields.assessment_headline && (t.assessmentStreamHeadline || t.headlineLabel)) {
        copy = (t.assessmentStreamHeadline || '').trim() || (t.phaseGenerating || '');
      } else if (fields.assessment_summary && t.assessmentStreamSummary) {
        copy = t.assessmentStreamSummary;
      } else if (Array.isArray(fields.recommended_products) && fields.recommended_products.length && t.assessmentStreamProducts) {
        copy = t.assessmentStreamProducts;
      }
    } else if (eventName === 'model_output_progress') {
      copy = t.assessmentStreamComposing || t.phaseGenerating || '';
    }
    if (!copy) return;
    const stream = instance.querySelector('[data-wait-stream]');
    if (stream && document.contains(stream)) {
      stream.textContent = copy;
    }
  }

  function advanceAssessmentWaitFromStream(instance, eventName) {
    const wait = window.TaraConsultationWait;
    if (!wait || typeof wait.advanceAssessmentWaitProgress !== 'function') return;

    // `accepted` only confirms that the stream is connected. The progressive
    // result panel takes over after model output starts; terminal events finish
    // from their authoritative payload instead of a fabricated percentage.
    if (eventName === 'accepted') wait.advanceAssessmentWaitProgress(instance, 5);
  }

  // Progressive results panel — written field-by-field as the worker
  // emits partial_assessment snapshots so the customer watches their
  // diagnosis form rather than staring at a progress bar. Keeps the
  // wait UI mounted as a fallback; reveals the panel on
  // model_output_started.
  function escHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escAttr(value) {
    return escHtml(value);
  }

  function getProductCatalogIndex(instance) {
    if (instance._streamProductIndex) return instance._streamProductIndex;
    const catalog = Array.isArray(instance._productCatalog) ? instance._productCatalog : [];
    const index = Object.create(null);
    catalog.forEach(function indexProduct(product) {
      if (!product || typeof product !== 'object') return;
      const handle = (product.handle || '').toString().trim();
      if (!handle) return;
      index[handle] = product;
    });
    instance._streamProductIndex = index;
    return index;
  }

  function showStreamingPanel(instance) {
    const wait = instance.querySelector('[data-consultation-wait]');
    const panel = instance.querySelector('[data-consultation-stream-panel]');
    if (!panel) return null;
    if (instance._streamPanelActive) return panel;

    instance._streamPanelActive = true;
    if (wait) wait.hidden = true;
    panel.hidden = false;
    // The progress timer keeps running; the panel shows the same bar.
    return panel;
  }

  function setStreamFieldText(panel, fieldName, value) {
    const field = panel.querySelector('[data-stream-field="' + fieldName + '"]');
    if (!field) return false;
    const content = field.querySelector('[data-stream-content]');
    if (!content) return false;
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) {
      field.classList.remove('is-filled');
      content.hidden = true;
      content.textContent = '';
      return false;
    }
    if (content.textContent === text && field.classList.contains('is-filled')) return true;
    content.textContent = text;
    content.hidden = false;
    field.classList.add('is-filled');
    return true;
  }

  function buildStreamProductCardHtml(product, recommendation, t) {
    const handle = (product && product.handle) || (recommendation && recommendation.handle) || '';
    const title = (product && product.title) || handle || '';
    const url = (product && product.url) || '';
    const image = product && product.image;
    const imageAlt = (product && (product.image_alt || product.title)) || title;
    const stepLabel = (recommendation && recommendation.step_label) || '';
    const heroLabel = recommendation && recommendation.hero ? (t.heroProduct || '') : '';
    const eyebrow = stepLabel || heroLabel;

    const parts = [];
    if (image) {
      const imgTag = '<img class="consultation-stream-panel__product-media" src="' + escAttr(image) + '" alt="' + escAttr(imageAlt) + '" width="320" height="320" loading="lazy">';
      parts.push(url ? '<a href="' + escAttr(url) + '">' + imgTag + '</a>' : imgTag);
    } else {
      parts.push('<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--media"></div>');
    }
    if (eyebrow) {
      parts.push('<p class="consultation-stream-panel__product-step">' + escHtml(eyebrow) + '</p>');
    }
    if (title) {
      const titleHtml = url
        ? '<a href="' + escAttr(url) + '">' + escHtml(title) + '</a>'
        : escHtml(title);
      parts.push('<h3 class="consultation-stream-panel__product-title">' + titleHtml + '</h3>');
    }
    return parts.join('');
  }

  function renderStreamProducts(instance, panel, recommendations) {
    const grid = panel.querySelector('[data-stream-products-grid]');
    if (!grid) return false;

    const items = Array.isArray(recommendations) ? recommendations : [];
    const slots = Array.from(grid.querySelectorAll('[data-stream-product-slot]'));
    const targetCount = Math.max(slots.length, items.length, 4);

    while (grid.children.length < targetCount) {
      const skeleton = document.createElement('article');
      skeleton.className = 'consultation-stream-panel__product is-skeleton';
      skeleton.setAttribute('data-stream-product-slot', '');
      skeleton.innerHTML = (
        '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--media"></div>'
        + '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--line"></div>'
        + '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--line consultation-stream-panel__skeleton-line--short"></div>'
      );
      grid.appendChild(skeleton);
    }

    const productIndex = getProductCatalogIndex(instance);
    const t = instance.config?.i18n || {};
    let matched = 0;

    Array.from(grid.children).forEach(function applySlot(slot, index) {
      const recommendation = items[index] || null;
      if (!recommendation || !recommendation.handle) {
        slot.className = 'consultation-stream-panel__product is-skeleton';
        slot.innerHTML = (
          '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--media"></div>'
          + '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--line"></div>'
          + '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--line consultation-stream-panel__skeleton-line--short"></div>'
        );
        return;
      }
      const handle = recommendation.handle;
      const product = productIndex[handle];
      if (!product) {
        slot.className = 'consultation-stream-panel__product is-skeleton';
        slot.innerHTML = (
          '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--media"></div>'
          + '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--line"></div>'
          + '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--line consultation-stream-panel__skeleton-line--short"></div>'
        );
        return;
      }
      const html = buildStreamProductCardHtml(product, recommendation, t);
      if (slot.dataset.streamHandle !== handle) {
        slot.dataset.streamHandle = handle;
        slot.className = 'consultation-stream-panel__product is-real';
        slot.innerHTML = html;
      }
      matched++;
    });

    return matched > 0;
  }

  function applyResultsPanelSnapshot(instance, fields) {
    const panel = instance.querySelector('[data-consultation-stream-panel]');
    if (!panel) return;
    const safeFields = (fields && typeof fields === 'object') ? fields : {};
    const t = instance.config?.i18n || {};

    setStreamFieldText(panel, 'assessment_headline', safeFields.assessment_headline);
    setStreamFieldText(panel, 'assessment_summary', safeFields.assessment_summary);
    setStreamFieldText(panel, 'care_priority', safeFields.care_priority);
    setStreamFieldText(panel, 'key_insight', safeFields.key_insight);
    renderStreamProducts(instance, panel, safeFields.recommended_products);

    const progressText = panel.querySelector('[data-stream-progress-text]');
    if (progressText) {
      let copy = '';
      if (Array.isArray(safeFields.recommended_products) && safeFields.recommended_products.length) {
        copy = t.assessmentStreamProducts || '';
      } else if (safeFields.assessment_summary) {
        copy = t.assessmentStreamSummary || '';
      } else if (safeFields.assessment_headline) {
        copy = t.assessmentStreamHeadline || '';
      } else {
        copy = t.assessmentStreamComposing || '';
      }
      progressText.textContent = copy || '';
    }
  }

  function scheduleResultsPanelUpdate(instance, fields) {
    instance._pendingStreamFields = fields;
    if (instance._streamPanelRafId) return;
    const w = typeof window !== 'undefined' ? window : null;
    const schedule = w && typeof w.requestAnimationFrame === 'function'
      ? w.requestAnimationFrame.bind(w)
      : function fallback(cb) { return setTimeout(cb, 16); };
    instance._streamPanelRafId = schedule(function flushStreamUpdate() {
      instance._streamPanelRafId = 0;
      const pending = instance._pendingStreamFields;
      instance._pendingStreamFields = null;
      if (pending) applyResultsPanelSnapshot(instance, pending);
    });
  }

  function resetResultsPanel(instance) {
    const panel = instance.querySelector('[data-consultation-stream-panel]');
    if (!panel) return;
    instance._streamPanelActive = false;
    if (instance._streamPanelRafId) {
      const w = typeof window !== 'undefined' ? window : null;
      if (w && typeof w.cancelAnimationFrame === 'function') {
        w.cancelAnimationFrame(instance._streamPanelRafId);
      } else {
        clearTimeout(instance._streamPanelRafId);
      }
      instance._streamPanelRafId = 0;
    }
    instance._pendingStreamFields = null;
    panel.hidden = true;
    panel.querySelectorAll('[data-stream-field]').forEach(function clearField(field) {
      field.classList.remove('is-filled');
      const content = field.querySelector('[data-stream-content]');
      if (content) {
        content.hidden = true;
        content.textContent = '';
      }
    });
    const grid = panel.querySelector('[data-stream-products-grid]');
    if (grid) {
      Array.from(grid.children).forEach(function resetSlot(slot) {
        slot.className = 'consultation-stream-panel__product is-skeleton';
        delete slot.dataset.streamHandle;
        slot.innerHTML = (
          '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--media"></div>'
          + '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--line"></div>'
          + '<div class="consultation-stream-panel__skeleton consultation-stream-panel__skeleton--line consultation-stream-panel__skeleton-line--short"></div>'
        );
      });
    }
    const progressText = panel.querySelector('[data-stream-progress-text]');
    if (progressText) progressText.textContent = '';
  }

  function applyStreamPanelEvent(instance, eventName, data) {
    if (eventName === 'model_output_started') {
      showStreamingPanel(instance);
      return;
    }
    if (eventName === 'partial_assessment') {
      const fields = (data && data.fields) || null;
      if (!fields) return;
      if (!instance._streamPanelActive) showStreamingPanel(instance);
      scheduleResultsPanelUpdate(instance, fields);
      return;
    }
    if (eventName === 'cache_hit' || eventName === 'final') {
      const payload = data && typeof data === 'object'
        ? (data.payload != null ? data.payload : data)
        : null;
      if (payload && typeof payload === 'object') {
        if (!instance._streamPanelActive) showStreamingPanel(instance);
        scheduleResultsPanelUpdate(instance, payload);
      }
      return;
    }
  }

  async function streamAssessmentAsync(instance, body, abort, finishAssessmentTimer) {
    let partials = {};

    const result = await window.TaraConsultationApi.postEventStream({
      baseUrl: instance.config.workerUrl,
      path: '/api/assessment-stream',
      body,
      signal: abort.signal,
      fallbackMessage: 'Assessment stream failed',
      slowThresholdMs: 25000,
      finalEventNames: ['final', 'cache_hit'],
      startTimer: () => finishAssessmentTimer,
      timerExtra: { transport: 'stream' },
      onEvent: (eventName, data) => {
        try {
          if (eventName === 'partial_assessment' && data && data.fields) {
            partials = data.fields;
          }
          advanceAssessmentWaitFromStream(instance, eventName);
          applyStreamPreviewCopy(instance, eventName, data);
          applyStreamPanelEvent(instance, eventName, data);
        } catch (previewError) {
          // Preview is best-effort; never let UI hooks abort the stream
          if (typeof console !== 'undefined') {
            console.warn('[Tara Consultation] stream preview failed:', previewError?.message || previewError);
          }
        }
      }
    });

    // For cache_hit the worker emits the full payload directly; for the
    // standard path the `final` event payload includes the assessment.
    // postEventStream already unwrapped `event.data.payload` for us when
    // present, so `result.data` is the assessment in either case.
    instance._lastAssessmentStreamPartials = partials;
    return result;
  }

  async function fetchAssessmentAsync(instance) {
    instance._assessmentAbort?.abort();
    instance._assessmentAbort = new AbortController();
    armAssessmentRequestTimeout(instance);
    const locale = instance._getUiLocale();
    const finishAssessmentTimer = instance._startMonitoringTimer('assessment_fetch', {
      route: '/api/assessment',
      tags: { consultation_action: 'assessment_fetch' }
    });
    let assessmentDurationMs = 0;
    const requestBody = buildAssessmentRequestBody(instance, locale);
    const streamingEnabled = instance.config.assessmentStreaming !== false
      && window.TaraConsultationApi
      && typeof window.TaraConsultationApi.postEventStream === 'function';

    try {
      let data;
      let durationMs = 0;

      if (streamingEnabled) {
        try {
          const streamed = await streamAssessmentAsync(
            instance,
            requestBody,
            instance._assessmentAbort,
            finishAssessmentTimer
          );
          data = streamed.data;
          durationMs = streamed.durationMs;
        } catch (streamError) {
          if (!shouldFallbackFromStream(streamError)) throw streamError;
          instance._log?.('assessment-stream-fallback', {
            error: streamError?.message || String(streamError),
            error_code: streamError?.errorCode || null,
            status: streamError?.status || null
          });
          // Fall back to the non-streaming endpoint silently. User never
          // sees a streaming-specific error message.
          if (instance._assessmentAbort?.signal?.aborted) {
            instance._assessmentAbort = new AbortController();
          }
          const fallbackTimer = instance._startMonitoringTimer('assessment_fetch', {
            route: '/api/assessment',
            tags: { consultation_action: 'assessment_fetch_fallback' }
          });
          const fallback = await window.TaraConsultationApi.postJson({
            baseUrl: instance.config.workerUrl,
            path: '/api/assessment',
            body: requestBody,
            signal: instance._assessmentAbort.signal,
            fallbackMessage: 'Assessment request failed',
            slowThresholdMs: 25000,
            timerExtra: { transport: 'json_fallback' },
            startTimer: () => fallbackTimer
          });
          data = fallback.data;
          durationMs = fallback.durationMs;
        }
      } else {
        const result = await window.TaraConsultationApi.postJson({
          baseUrl: instance.config.workerUrl,
          path: '/api/assessment',
          body: requestBody,
          signal: instance._assessmentAbort.signal,
          fallbackMessage: 'Assessment request failed',
          slowThresholdMs: 25000,
          timerExtra: { transport: 'json' },
          startTimer: () => finishAssessmentTimer
        });
        data = result.data;
        durationMs = result.durationMs;
      }

      assessmentDurationMs = durationMs || assessmentDurationMs;

      if (data && data.session_id) {
        instance.sessionId = data.session_id;
        instance._setStoredSessionId(data.session_id);
      }

      return data;
    } catch (error) {
      assessmentDurationMs = error?.requestDurationMs || assessmentDurationMs;
      if (error?.name !== 'AbortError') {
        instance._captureMonitoringException(error, {
          route: '/api/assessment',
          error_code: error?.errorCode || 'assessment_request_failed',
          status_code: error?.status || null,
          request_duration_ms: assessmentDurationMs,
          worker_error: error?.workerError || error?.message || '',
          tags: { consultation_action: 'assessment_fetch' },
          fingerprint: ['tara-consultation', 'assessment-fetch', error?.errorCode || error?.name || 'error']
        });
      }
      throw error;
    } finally {
      clearAssessmentRequestTimeout(instance);
    }
  }

  function hasRenderableAssessment(result) {
    return !!(result && typeof result === 'object' && Object.keys(result).length > 0);
  }

  function resetAssessmentRetryState(instance) {
    nextAssessmentRequestId(instance);
    clearAssessmentRequestTimeout(instance);
    instance._assessmentAbort?.abort();
    instance._assessmentPromise = null;
    instance._assessmentResult = null;
    instance._assessmentError = null;
  }

  function showAssessmentFailure(instance, error, consultationAction, onRetry) {
    console.error('Consultation assessment failed:', error);
    instance._captureMonitoringException(error, {
      route: '/api/assessment',
      error_code: error?.errorCode || 'assessment_request_failed',
      status_code: error?.status || null,
      worker_error: error?.workerError || error?.message || '',
      tags: { consultation_action: consultationAction },
      fingerprint: ['tara-consultation', consultationAction, error?.errorCode || error?.name || 'error']
    });

    resetAssessmentRetryState(instance);
    resetResultsPanel(instance);
    instance._renderAssessmentFailure(() => {
      instance._renderAssessmentWait(instance._waitExperience);
      if (typeof onRetry === 'function') onRetry();
    }, error);
  }

  function isRateLimitError(error) {
    const code = String(error?.errorCode || error?.code || '').trim();
    return error?.status === 429 || code.indexOf('rate_limited_') === 0;
  }

  async function onEmailSubmit(instance, event) {
    event.preventDefault();
    const contactInput = instance._getContactInput();
    if (!contactInput) return;

    const submitButton = event.submitter || instance.querySelector('[data-email-form] button[type="submit"]');
    const now = Date.now();
    if (instance._contactSubmitLockedUntil && now < instance._contactSubmitLockedUntil) return;
    instance._contactSubmitLockedUntil = now + 300;
    if (submitButton) submitButton.disabled = true;

    const contact = instance._parseContactValue(contactInput.value, {
      allowEmpty: !instance._isContactCaptureRequired()
    });
    if (contact.empty && !instance._isContactCaptureRequired()) {
      instance._continueWithoutContact();
      return;
    }
    if (!contact.valid) {
      contactInput.setCustomValidity(instance._getInvalidContactMessage(contact));
      contactInput.reportValidity();
      if (submitButton) submitButton.disabled = false;
      return;
    }

    contactInput.setCustomValidity('');
    instance._syncContactInput();

    const subscribe = instance.querySelector('[name="subscribe"]')?.checked || false;

    instance._contact = contact;
    instance._email = contact.type === 'email' ? contact.value : null;
    instance._contactOffer = null;

    const fbPayload = instance._getFacebookTrackingPayload();

    if (contact.type === 'email' && subscribe) {
      instance._identifyKlaviyo(contact.value, true);
    }

    const contactCapturePromise = sendContactCapture(instance, contact, subscribe, fbPayload);
    instance._renderAssessmentWait(instance._waitExperience);
    instance._showStep('assessment-loading');

    try {
      let assessmentPromise;
      if (instance._assessmentPromise) {
        assessmentPromise = instance._assessmentPromise.then(() => instance._assessmentResult);
      } else if (instance._assessmentResult) {
        assessmentPromise = Promise.resolve(instance._assessmentResult);
      } else {
        assessmentPromise = fireAssessmentEarly(instance);
      }

      const [assessmentState, contactCaptureState] = await Promise.allSettled([
        assessmentPromise,
        contactCapturePromise
      ]);

      await instance._ensureAssessmentWaitMinimum();

      let finalResult = assessmentState.status === 'fulfilled' && hasRenderableAssessment(assessmentState.value)
        ? assessmentState.value
        : (hasRenderableAssessment(instance._assessmentResult) ? instance._assessmentResult : null);

      if (!finalResult && instance._assessmentError) {
        throw instance._assessmentError;
      }

      if (contactCaptureState.status === 'fulfilled' && contactCaptureState.value) {
        instance._contactOffer = contactCaptureState.value.offer || null;
        const contactAssessment = contactCaptureState.value.assessment
          || contactCaptureState.value.assessmentResult
          || null;
        if (hasRenderableAssessment(contactAssessment)) {
          finalResult = contactAssessment;
        }
      } else if (
        contactCaptureState.status === 'rejected'
        && isRateLimitError(contactCaptureState.reason)
        && !finalResult
      ) {
        throw contactCaptureState.reason;
      }

      if (!finalResult) {
        throw new Error('Assessment payload missing');
      }

      await instance._completeAssessmentWaitProgress();
      instance._assessmentResult = finalResult;
      instance._storeAndRedirect(finalResult, contact);
    } catch (error) {
      if (error.name === 'AbortError' && error.errorCode !== 'request_timeout') return;
      await contactCapturePromise.catch(() => null);
      showAssessmentFailure(instance, error, 'assessment_submit', () => instance._waitForAssessmentAndRedirect());
    }
  }

  function normalizeOfferDiscountPercent(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  function getOfferSettingsPayload(instance) {
    return {
      offer_discount_for_full_set: !!instance.config.offerDiscountForFullSet,
      full_set_discount_percent: normalizeOfferDiscountPercent(instance.config.fullSetDiscountPercent)
    };
  }

  function getLoggedInCustomerPayload(instance) {
    const customerId = instance?.config?.shopifyCustomerId;
    const customerEmail = typeof instance?.config?.shopifyCustomerEmail === 'string'
      ? instance.config.shopifyCustomerEmail.trim()
      : instance?.config?.shopifyCustomerEmail;

    if (!customerId || !customerEmail) return null;

    return {
      shopify_customer_id: customerId,
      shopify_customer_email: customerEmail
    };
  }

  async function sendContactCapture(instance, contact, subscribe, fbPayload) {
    if (!instance.config.workerUrl) return null;
    const offerSettings = getOfferSettingsPayload(instance);
    const contactAbort = new AbortController();
    const contactDeadline = createContactCaptureDeadline(contactAbort);
    const finishContactTimer = instance._startMonitoringTimer('contact_capture', {
      route: '/api/email',
      tags: { consultation_action: 'contact_capture' },
      extra: { contact_type: contact?.type || null }
    });
    let contactDurationMs = 0;

    try {
      const locale = instance._getUiLocale();
      const payload = {
        session_id: instance._ensureSessionId(),
        conversation_history: instance.conversationHistory,
        subscribe_marketing: subscribe,
        provider: instance.config.modelProvider || 'openai',
        ...instance._buildWorkerContextPayload(locale),
        fb: fbPayload,
        ...offerSettings,
        contact_type: contact?.type || null
      };
      const customerPayload = getLoggedInCustomerPayload(instance);
      if (customerPayload) {
        payload.shopify_customer_id = customerPayload.shopify_customer_id;
        payload.shopify_customer_email = customerPayload.shopify_customer_email;
      }

      if (contact?.type === 'email') {
        payload.email = contact.value;
      } else if (contact?.type === 'mobile') {
        payload.contact = contact.value;
        payload.mobile = contact.normalizedValue || contact.value;
        payload.phone = contact.value;
        payload.whatsapp_number = contact.normalizedValue || contact.value;
      }

      const contactRequest = window.TaraConsultationApi.postJson({
        baseUrl: instance.config.workerUrl,
        path: '/api/email',
        body: payload,
        signal: contactAbort.signal,
        fallbackMessage: 'Contact capture request failed',
        slowThresholdMs: 12000,
        startTimer: () => finishContactTimer
      });
      const { data, durationMs } = await Promise.race([contactRequest, contactDeadline.promise]);

      contactDurationMs = durationMs || contactDurationMs;

      if (data?.session_id) {
        instance.sessionId = data.session_id;
        instance._setStoredSessionId(data.session_id);
      }

      const normalizedOffer = data?.offer ? instance._normalizeOfferPayload(data.offer) : null;

      if (offerSettings.offer_discount_for_full_set) {
        const offerState = data?.offer_state && typeof data.offer_state === 'object' ? data.offer_state : null;
        const offerStateStatus = (offerState?.status || '').toString().trim().toLowerCase();
        const offerStateReason = (offerState?.reason || '').toString().trim().toLowerCase();
        const activationStatus = (normalizedOffer?.activation?.status || '').toString().trim().toLowerCase();
        const activationCode = (normalizedOffer?.activation?.code || '').toString().trim();
        const unexpectedState = ['error', 'failed', 'activation_failed'].includes(offerStateStatus)
          || ['error', 'failed', 'activation_failed'].includes(offerStateReason);

        if ((activationStatus !== 'active' || !activationCode) && unexpectedState) {
          instance._captureMonitoringMessage('Full-set offer was requested but not activated during contact capture', {
            route: '/api/email',
            error_code: 'full_set_offer_missing',
            level: 'warning',
            status_code: 200,
            request_duration_ms: contactDurationMs,
            tags: {
              consultation_action: 'contact_capture',
              contact_type: contact?.type || null,
              offer_requested: 'true',
              offer_activation_status: activationStatus || 'missing',
              offer_state_status: offerStateStatus || 'missing'
            },
            extra: {
              offer_present: Boolean(normalizedOffer),
              offer_code_present: Boolean(activationCode),
              offer_mode: normalizedOffer?.mode || '',
              offer_state_reason: offerStateReason || '',
              discount_percent: normalizedOffer?.discount_percent || normalizedOffer?.discountPercent || offerSettings.full_set_discount_percent || 0,
              response_keys: data && typeof data === 'object' ? Object.keys(data) : []
            },
            fingerprint: ['tara-consultation', 'full-set-offer-missing', activationStatus || 'missing']
          });
        }
      }

      return {
        offer: normalizedOffer,
        offerState: data?.offer_state || null,
        assessmentResult: data?.assessment_result || null,
        assessment: data?.assessment && typeof data.assessment === 'object' ? data.assessment : null,
        resultUrl: typeof data?.result_url === 'string' ? data.result_url : ''
      };
    } catch (error) {
      contactDurationMs = error?.requestDurationMs || contactDurationMs;
      if (error?.name !== 'AbortError') {
        instance._captureMonitoringException(error, {
          route: '/api/email',
          error_code: error?.errorCode || 'contact_capture_failed',
          status_code: error?.status || null,
          request_duration_ms: contactDurationMs,
          worker_error: error?.workerError || error?.message || '',
          tags: {
            consultation_action: 'contact_capture',
            contact_type: contact?.type || null
          },
          fingerprint: ['tara-consultation', 'contact-capture', error?.errorCode || error?.name || 'error']
        });
      }
      if (error?.status === 429 || String(error?.errorCode || error?.code || '').indexOf('rate_limited_') === 0) {
        throw error;
      }
    } finally {
      contactDeadline.clear();
    }
    return null;
  }

  function buildStoredContactPayload(contact) {
    if (!contact || !contact.type || !contact.value) return null;

    const storedContact = {
      type: contact.type,
      value: contact.value
    };

    if (contact.normalizedValue) {
      storedContact.normalized_value = contact.normalizedValue;
    }

    return storedContact;
  }

  function buildStoredAssessmentPayload(instance, result, contact) {
    return {
      assessment: result,
      store: instance._buildStorePayload(),
      contact: buildStoredContactPayload(contact)
    };
  }

  function storeAndRedirect(instance, result, contact) {
    const finalResult = instance._mergeOfferIntoAssessment(result, instance._contactOffer);
    if (!hasRenderableAssessment(finalResult)) return;

    if (!instance._completionTracked) {
      instance._track('ConsultationCompleted', {
        content_name: 'Tara Hair Consultation',
        total_questions: instance.questionCount,
        contact_type: contact?.type || null
      });

      instance._completionTracked = true;
    }

    if (contact) {
      finalResult.contact = {
        type: contact.type,
        value: contact.value
      };

      if (contact.normalizedValue) {
        finalResult.contact.normalized_value = contact.normalizedValue;
      }

      if (contact.type === 'email') {
        finalResult.email = contact.value;
      } else if (contact.type === 'mobile') {
        finalResult.mobile = contact.normalizedValue || contact.value;
        finalResult.phone = contact.value;
        finalResult.whatsapp_number = contact.normalizedValue || contact.value;
      }
    }

    clearProgressState(instance);
    instance._setStoredAssessment(buildStoredAssessmentPayload(instance, finalResult, contact));
    goToResults(instance);
  }

  function goToResults(instance) {
    const url = instance.config.resultsPageUrl || '/pages/consultation-results';
    window.location.href = instance._resolveLocalizedPath(url);
  }

  function animateLoadingSteps(instance) {
    const steps = instance.querySelectorAll('[data-loading-step]');
    let currentIdx = 0;
    const lastIdx = steps.length - 1;

    const advance = () => {
      if (instance.currentStep !== 'assessment-loading') return;
      steps.forEach((step, index) => {
        step.classList.remove('consultation-loading__step--active', 'consultation-loading__step--done');
        if (index < currentIdx) step.classList.add('consultation-loading__step--done');
        if (index === currentIdx) step.classList.add('consultation-loading__step--active');
      });
      if (currentIdx < lastIdx) {
        currentIdx++;
        setTimeout(advance, 3000);
      }
    };

    advance();
  }

  function retake(instance) {
    instance.abortController?.abort();
    resetAssessmentRetryState(instance);
    resetResultsPanel(instance);
    instance._requestId++;

    instance.conversationHistory = [];
    instance.questionCount = 0;
    instance._lastQuestionComplete = false;
    instance._lastAdaptiveResponse = null;
    instance._inflight = false;
    instance._waitExperience = null;
    instance._assessmentWaitRunId++;
    instance._prefetchedQuestion = null;
    instance._prefetchedRequestId = 0;
    instance._completionTracked = false;
    instance.sessionId = null;
    instance._anchorSelectedValue = null;
    instance._anchorSelectedLabel = null;
    instance._email = null;
    instance._contact = null;
    instance._contactOffer = null;

    instance.querySelectorAll('[data-step="anchor"] .consultation-option').forEach((option) => {
      option.classList.remove('consultation-option--selected');
      option.setAttribute('aria-checked', 'false');
    });

    const intakeText = instance.querySelector('[data-intake-text]');
    if (intakeText) {
      intakeText.value = '';
      intakeText.disabled = false;
    }
    instance._resetAnchorVoiceNudge();

    const contactInput = instance._getContactInput();
    if (contactInput) {
      contactInput.value = '';
      contactInput.setCustomValidity('');
      instance._syncContactInput();
    }

    const adaptiveTextarea = instance.querySelector('[data-adaptive-textarea]');
    if (adaptiveTextarea) {
      adaptiveTextarea.value = '';
      adaptiveTextarea.disabled = false;
      adaptiveTextarea.style.height = '';
      delete adaptiveTextarea.dataset.selectedSuggestion;
    }

    const optionsEl = instance.querySelector('[data-adaptive-options]');
    if (optionsEl) {
      optionsEl.innerHTML = '';
      optionsEl.hidden = true;
    }
    const adaptiveTextBlock = instance.querySelector('[data-adaptive-text]');
    if (adaptiveTextBlock) adaptiveTextBlock.hidden = true;
    instance._renderAdaptiveSuggestions([]);

    instance.querySelectorAll('button, textarea, input').forEach((element) => {
      element.disabled = false;
      element.removeAttribute('tabindex');
    });

    clearStoredState(instance);
    instance._showStep('anchor', 'back');
  }

  function clearStoredState(instance) {
    if (window.TaraConsultationSession && typeof window.TaraConsultationSession.clearStoredState === 'function') {
      window.TaraConsultationSession.clearStoredState();
    }
  }

  function clearProgressState(instance) {
    void instance;
    if (window.TaraConsultationSession && typeof window.TaraConsultationSession.clearProgressState === 'function') {
      window.TaraConsultationSession.clearProgressState();
    }
  }

  function collectAssessmentRecordCandidates(record) {
    const candidates = [];
    const queue = [record];
    const seen = new Set();
    const nestedKeys = [
      'assessment',
      'assessment_result',
      'assessmentResult',
      'result',
      'result_payload',
      'resultPayload',
      'profile',
      'payload',
      'data'
    ];

    while (queue.length && candidates.length < 16) {
      const item = queue.shift();
      if (!item || typeof item !== 'object' || seen.has(item)) continue;
      seen.add(item);
      candidates.push(item);

      nestedKeys.forEach(function enqueueNested(key) {
        if (item[key] && typeof item[key] === 'object') queue.push(item[key]);
      });
    }

    return candidates;
  }

  function pickRecordText(candidates, keys) {
    for (const candidate of candidates) {
      for (const key of keys) {
        const value = candidate[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
        if (typeof value === 'number' && Number.isFinite(value)) return String(value);
      }
    }
    return '';
  }

  function getCompletedAssessmentRecord(record) {
    const candidates = collectAssessmentRecordCandidates(record);
    if (!candidates.length) return null;

    const assessmentId = pickRecordText(candidates, ['assessment_id', 'assessmentId']);
    const resultUrl = pickRecordText(candidates, ['result_url', 'resultUrl']);

    if (!assessmentId || !resultUrl) return null;

    return {
      assessment_id: assessmentId,
      result_url: resultUrl,
      completed_at: pickRecordText(candidates, ['completed_at', 'completedAt', 'created_at', 'createdAt'])
    };
  }

  function getRawStoredAssessmentRecord(instance) {
    const session = window.TaraConsultationSession || null;
    if (session && typeof session.readAssessmentRecord === 'function') {
      return session.readAssessmentRecord();
    }
    return instance._getStoredAssessment();
  }

  function hasStoredAssessmentContent(record) {
    return collectAssessmentRecordCandidates(record).some(function hasContent(candidate) {
      return Boolean(
        candidate.assessment_headline
        || candidate.assessment_summary
        || candidate.care_priority
        || candidate.routine_purchase_option
        || candidate.offer
        || (Array.isArray(candidate.recommended_products) && candidate.recommended_products.length)
      );
    });
  }

  function formatResumeDate(instance, dateValue) {
    if (!dateValue) return '';

    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return '';

    try {
      return new Intl.DateTimeFormat(instance._getUiLocale(), {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(parsed);
    } catch (error) {
      return parsed.toLocaleDateString();
    }
  }

  function getResumeCopy(instance, completedAt) {
    const t = instance.config?.i18n || {};
    const date = formatResumeDate(instance, completedAt);
    const bodyTemplate = t.resumeBody || 'You already completed a consultation on {{ date }}. View those results, or start fresh?';
    return {
      title: t.resumeTitle || 'Welcome back.',
      body: bodyTemplate.replace('{{ date }}', date || ''),
      viewResults: t.resumeViewResults || 'View my results',
      startFresh: t.resumeStartFresh || 'Start fresh',
      allConsultations: t.resumeAllConsultations || 'See all my consultations'
    };
  }

  function normalizePastConsultations(value) {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    return value.filter(function keepValidConsultation(item) {
      if (!item || typeof item !== 'object') return false;
      const id = String(item.assessment_id || item.assessmentId || '').trim();
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  function getInitialPastConsultations(instance) {
    return normalizePastConsultations(instance?.config?.shopifyCustomerConsultations);
  }

  function getStoreCode(instance) {
    const store = typeof instance._getStoreConfig === 'function' ? instance._getStoreConfig() : {};
    return store.key || store.market || '';
  }

  async function fetchPastConsultations(instance) {
    if (!instance?.config?.workerUrl || !instance.config.shopifyCustomerId || !instance.config.shopifyCustomerEmail) {
      return [];
    }

    try {
      const result = await window.TaraConsultationApi.postJson({
        baseUrl: instance.config.workerUrl,
        path: '/api/my-consultations',
        body: {
          shopify_customer_id: instance.config.shopifyCustomerId,
          email: instance.config.shopifyCustomerEmail,
          store_code: getStoreCode(instance)
        }
      });

      const payload = result.data;
      return normalizePastConsultations(payload?.consultations);
    } catch (error) {
      instance._log?.('past-consultations-fetch-failed', { message: error?.message || String(error) });
      return [];
    }
  }

  function assignLocation(instance, url) {
    if (instance && typeof instance._assignLocation === 'function') {
      instance._assignLocation(url);
      return;
    }
    window.location.assign(url);
  }

  function getResumeResultsPageUrl(instance) {
    const url = instance?.config?.resultsPageUrl || '/pages/consultation-results';
    return typeof instance?._resolveLocalizedPath === 'function'
      ? instance._resolveLocalizedPath(url)
      : url;
  }

  async function hydrateStoredAssessmentForResume(instance, completedRecord) {
    const storedRecord = getRawStoredAssessmentRecord(instance);
    if (hasStoredAssessmentContent(storedRecord)) return;

    const resultUrl = completedRecord && completedRecord.result_url;
    if (!resultUrl) return;

    const response = await fetch(resultUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'omit'
    });
    if (!response.ok) throw new Error(`Stored result request failed (${response.status})`);

    const assessment = await response.json();
    if (!assessment || typeof assessment !== 'object') throw new Error('Stored result payload missing');

    instance._setStoredAssessment({
      assessment,
      store: storedRecord && storedRecord.store ? storedRecord.store : instance._buildStorePayload(),
      contact: storedRecord && storedRecord.contact ? storedRecord.contact : null
    });
  }

  async function openResumeResults(instance, completedRecord) {
    const resultsPageUrl = getResumeResultsPageUrl(instance);

    try {
      await hydrateStoredAssessmentForResume(instance, completedRecord);
    } catch (error) {
      instance._captureMonitoringException?.(error, {
        route: completedRecord?.result_url || '/results',
        error_code: 'resume_result_hydration_failed',
        tags: { consultation_action: 'resume_results' },
        fingerprint: ['tara-consultation', 'resume-result-hydration']
      });
    }

    assignLocation(instance, resultsPageUrl);
  }

  function reloadForResume(instance) {
    if (instance && typeof instance._reloadForResumeModal === 'function') {
      instance._reloadForResumeModal();
      return;
    }
    window.location.reload();
  }

  function startFresh(instance) {
    clearStoredState(instance);
    assignLocation(instance, `${window.location.pathname}?restart=1`);
  }

  function setAllConsultationsButtonVisibility(modal, visible) {
    const button = modal?.querySelector('[data-resume-all-consultations]');
    if (!button) return;
    button.hidden = !visible;
  }

  async function hydrateResumeModalActions(instance, modal) {
    if (!instance?.config?.shopifyCustomerId || !instance.config.shopifyCustomerEmail) return;
    const initial = getInitialPastConsultations(instance);
    if (initial.length >= 2) {
      setAllConsultationsButtonVisibility(modal, true);
      return;
    }

    const remote = await fetchPastConsultations(instance);
    setAllConsultationsButtonVisibility(modal, remote.length >= 2);
  }

  function showResumeModal(instance, completedRecord) {
    if (instance._resumeModalShown) return;
    instance._resumeModalShown = true;

    const copy = getResumeCopy(instance, completedRecord.completed_at);
    const direction = document.documentElement.dir === 'rtl' || String(instance._getUiLocale()).toLowerCase().startsWith('ar')
      ? 'rtl'
      : 'ltr';
    const modal = document.createElement('div');
    modal.className = 'consultation-resume-modal';
    modal.setAttribute('data-consultation-resume-modal', '');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'consultation-resume-title');
    modal.setAttribute('dir', direction);
    modal.innerHTML = `
      <button class="consultation-resume-modal__backdrop" type="button" data-resume-start-fresh aria-label="${instance._escapeAttr(copy.startFresh)}"></button>
      <div class="consultation-resume-modal__card">
        <p class="consultation-resume-modal__eyebrow">${instance._escapeHtml(instance.config?.i18n?.serviceConsultation || 'Consultation')}</p>
        <h2 class="consultation-resume-modal__title" id="consultation-resume-title">${instance._escapeHtml(copy.title)}</h2>
        <p class="consultation-resume-modal__body">${instance._escapeHtml(copy.body)}</p>
        <div class="consultation-resume-modal__actions">
          <button class="consultation-resume-modal__button consultation-resume-modal__button--primary" type="button" data-resume-view-results>${instance._escapeHtml(copy.viewResults)}</button>
          <button class="consultation-resume-modal__button consultation-resume-modal__button--secondary" type="button" data-resume-start-fresh>${instance._escapeHtml(copy.startFresh)}</button>
          <button class="consultation-resume-modal__link" type="button" data-resume-all-consultations hidden>${instance._escapeHtml(copy.allConsultations)}</button>
        </div>
      </div>
    `;

    modal.addEventListener('click', function onResumeModalClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-resume-view-results]')) {
        openResumeResults(instance, completedRecord);
        return;
      }
      if (target.closest('[data-resume-all-consultations]')) {
        assignLocation(instance, instance._resolveLocalizedPath('/pages/my-consultations'));
        return;
      }
      if (target.closest('[data-resume-start-fresh]')) {
        startFresh(instance);
      }
    });

    modal.addEventListener('keydown', function onResumeModalKeydown(event) {
      if (event.key === 'Escape') startFresh(instance);
    });

    document.body.appendChild(modal);
    setAllConsultationsButtonVisibility(modal, getInitialPastConsultations(instance).length >= 2);
    requestAnimationFrame(function focusResumeModal() {
      modal.querySelector('[data-resume-view-results]')?.focus();
    });
    hydrateResumeModalActions(instance, modal);
  }

  function getMidFlowProgressRecord() {
    const session = window.TaraConsultationSession || null;
    if (!session || typeof session.getProgressState !== 'function') return null;
    return session.getProgressState(MID_FLOW_RESUME_MAX_AGE_MS);
  }

  function getMidFlowResumeCopy(instance, progressRecord) {
    const t = instance.config?.i18n || {};
    const turn = Array.isArray(progressRecord?.history) ? progressRecord.history.length : 0;
    const bodyTemplate = t.midFlowResumeBody || 'You were on turn {{ turn }}. Continue from there, or start fresh?';
    return {
      title: t.midFlowResumeTitle || 'Continue your consultation?',
      body: bodyTemplate.replace('{{ turn }}', String(turn || 1)),
      continueLabel: t.midFlowResumeContinue || 'Continue',
      startFresh: t.midFlowResumeStartFresh || t.resumeStartFresh || 'Start fresh',
    };
  }

  function continueMidFlow(instance, progressRecord, modal) {
    if (!progressRecord || !Array.isArray(progressRecord.history) || !progressRecord.history.length) {
      startFresh(instance);
      return;
    }

    modal?.remove();
    instance._resumeModalShown = false;
    instance.sessionId = progressRecord.sessionId || instance._getStoredSessionId?.() || null;
    instance.conversationHistory = progressRecord.history;
    instance.questionCount = progressRecord.history.length;
    instance._lastQuestionComplete = false;
    instance._lastAdaptiveResponse = null;
    instance._prefetchedQuestion = null;
    instance._prefetchedRequestId = 0;
    instance._assessmentPromise = null;
    instance._assessmentResult = null;
    instance._assessmentError = null;
    instance._setConversationHistory(instance.conversationHistory);
    instance._showStep('question-loading', 'forward');
    instance._fetchNextQuestion('forward');
  }

  function showMidFlowResumeModal(instance, progressRecord) {
    if (instance._resumeModalShown) return;
    instance._resumeModalShown = true;

    const copy = getMidFlowResumeCopy(instance, progressRecord);
    const direction = document.documentElement.dir === 'rtl' || String(instance._getUiLocale()).toLowerCase().startsWith('ar')
      ? 'rtl'
      : 'ltr';
    const modal = document.createElement('div');
    modal.className = 'consultation-resume-modal consultation-resume-modal--mid-flow';
    modal.setAttribute('data-consultation-progress-modal', '');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'consultation-progress-resume-title');
    modal.setAttribute('dir', direction);
    modal.innerHTML = `
      <button class="consultation-resume-modal__backdrop" type="button" data-progress-start-fresh aria-label="${instance._escapeAttr(copy.startFresh)}"></button>
      <div class="consultation-resume-modal__card">
        <p class="consultation-resume-modal__eyebrow">${instance._escapeHtml(instance.config?.i18n?.serviceConsultation || 'Consultation')}</p>
        <h2 class="consultation-resume-modal__title" id="consultation-progress-resume-title">${instance._escapeHtml(copy.title)}</h2>
        <p class="consultation-resume-modal__body">${instance._escapeHtml(copy.body)}</p>
        <div class="consultation-resume-modal__actions">
          <button class="consultation-resume-modal__button consultation-resume-modal__button--primary" type="button" data-progress-continue>${instance._escapeHtml(copy.continueLabel)}</button>
          <button class="consultation-resume-modal__button consultation-resume-modal__button--secondary" type="button" data-progress-start-fresh>${instance._escapeHtml(copy.startFresh)}</button>
        </div>
      </div>
    `;

    modal.addEventListener('click', function onProgressModalClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-progress-continue]')) {
        continueMidFlow(instance, progressRecord, modal);
        return;
      }
      if (target.closest('[data-progress-start-fresh]')) {
        startFresh(instance);
      }
    });

    modal.addEventListener('keydown', function onProgressModalKeydown(event) {
      if (event.key === 'Escape') startFresh(instance);
    });

    document.body.appendChild(modal);
    requestAnimationFrame(function focusProgressModal() {
      modal.querySelector('[data-progress-continue]')?.focus();
    });
  }

  function handleAlreadyCompletedResponse(instance, data) {
    const completedRecord = getCompletedAssessmentRecord(data);
    if (!completedRecord) {
      clearStoredState(instance);
      instance._renderQuestionRequestError?.('forward');
      return;
    }

    instance._setStoredAssessment({
      assessment_id: completedRecord.assessment_id,
      result_url: completedRecord.result_url,
      completed_at: completedRecord.completed_at || data.completed_at || '',
      session_id: data.session_id || instance.sessionId || ''
    });
    reloadForResume(instance);
  }

  function restoreState(instance) {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('retake') === '1' || params.get('restart') === '1') {
      clearStoredState(instance);
      return;
    }

    const session = window.TaraConsultationSession || null;
    let storedRecord = session && typeof session.readAssessmentRecord === 'function'
      ? session.readAssessmentRecord()
      : instance._getStoredAssessment();
    if (!storedRecord) storedRecord = instance._getStoredAssessment();
    const completedRecord = getCompletedAssessmentRecord(storedRecord);
    if (completedRecord) {
      showResumeModal(instance, completedRecord);
      return;
    }

    const progressRecord = getMidFlowProgressRecord();
    if (progressRecord) {
      showMidFlowResumeModal(instance, progressRecord);
    } else if (session && typeof session.clearProgressState === 'function') {
      session.clearProgressState();
    }
  }

  function resetViewportOnLoad(instance) {
    if (instance.currentStep !== 'anchor' || window.location.hash) return;
    if (window.scrollY <= 0) return;

    const scrollToTop = () => {
      if (instance.currentStep !== 'anchor') return;
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    requestAnimationFrame(() => requestAnimationFrame(scrollToTop));
  }

  window.TaraConsultationCompletion = Object.freeze({
    fireAssessmentEarly,
    fetchAssessmentAsync,
    onEmailSubmit,
    resetAssessmentRetryState,
    normalizeOfferDiscountPercent,
    getOfferSettingsPayload,
    getLoggedInCustomerPayload,
    sendContactCapture,
    buildStoredContactPayload,
    buildStoredAssessmentPayload,
    storeAndRedirect,
    goToResults,
    animateLoadingSteps,
    retake,
    clearStoredState,
    clearProgressState,
    getCompletedAssessmentRecord,
    showResumeModal,
    showMidFlowResumeModal,
    handleAlreadyCompletedResponse,
    restoreState,
    resetViewportOnLoad,
    applyStreamPanelEvent,
    applyResultsPanelSnapshot,
    resetResultsPanel,
  });
})();
